import type { Rule, RuleGroup } from '@/api/types';

/**
 * The shape this needs from an audience. Deliberately structural rather than the
 * full `Audience`, so the list screens, the drawer and the tests can all pass what
 * they already hold.
 */
export interface SeedDependentCandidate {
  id: string;
  name: string;
  audience_type: string;
  archived_at?: number | null;
  rules: RuleGroup;
}

function collectSeeds(group: RuleGroup, into: Set<string>): void {
  for (const rule of group.rules as Rule[]) {
    if (rule.kind === 'lookalike') {
      into.add(rule.seed);
    } else if (rule.kind === 'group') {
      collectSeeds(rule, into);
    }
  }
}

/**
 * Every seed named anywhere in a rule tree, at any depth.
 *
 * Walks nested groups rather than reading a fixed path: the two shapes the visual
 * builder produces put the leaf at `rules[0]` or `rules[0].rules[0]`, and a reader
 * that hard-codes either misses the other.
 */
export function seedsNamedBy(rules: RuleGroup): string[] {
  const seeds = new Set<string>();
  collectSeeds(rules, seeds);
  return [...seeds];
}

/**
 * The live lookalikes that would be re-fitted if `audienceId`'s definition changed.
 *
 * Editing a seed unpublishes every dependent lookalike's fit until the nightly refit
 * lands — the fit is joined to the seed's revision, so a bump invalidates it
 * atomically. That is a real cost and the operator should see it before saving, but
 * it is not a reason to refuse the edit: nothing is minted, no provider container
 * moves, and the dependent's own row is untouched.
 *
 * Archived dependents are excluded for the same reason the D1 guard excludes them —
 * an archived lookalike references nothing that can still be materialized.
 */
export function lookalikeSeedDependents<T extends SeedDependentCandidate>(
  audienceId: string,
  audiences: readonly T[] = [],
): T[] {
  if (!audienceId) return [];
  return audiences.filter(
    (audience) =>
      audience.id !== audienceId
      && !audience.archived_at
      && audience.audience_type === 'lookalike'
      && seedsNamedBy(audience.rules).includes(audienceId),
  );
}
